import { BrowserProvider, JsonRpcSigner, Signer, toQuantity } from 'ethers';

import { IS_TEST_ENVIRONMENT } from 'config';
import { ChainConfig } from 'config/configType';
import { NATIVE_ASSET_DECIMALS } from 'config/constants';
import { waitAsync } from 'lib/promiseTools';
import { IWallet } from 'reduxState/wallet/walletReducer';
import { WalletType } from 'reduxState/wallet/walletUtils';

import {
  detectSoftwareWalletChainId,
  getWeb3Async,
  resetWeb3,
} from './softwareWallet';
import { initializeWalletConnect } from './walletConnectV2';

import { getSigner } from '.';

import type EthereumProviderType from '@walletconnect/ethereum-provider';

/**
 * Note: WalletConnect is listening directly in connector (see walletConnect.ts)
 */
export async function detectWalletNetworkChainIdAsync(
  wallet: IWallet,
  prioritySigner?: JsonRpcSigner
) {
  const { walletType } = wallet;
  if (
    walletType === 'privateKey' ||
    walletType === 'keystore' ||
    walletType === 'qrSynced' ||
    !walletType
  ) {
    return;
  }
  if (walletType?.startsWith('softwareWeb3')) {
    // To start watcher listener
    return getWeb3Async(walletType)
      .then(() => detectSoftwareWalletChainId())
      .then(detectedChainId => {
        if (!detectedChainId) {
          return undefined;
        }
        return Number(detectedChainId);
      });
  }
  // Other than web3 software wallet. One time check
  const signer = prioritySigner
    ? prioritySigner
    : await getSigner(wallet, undefined, false);
  if (!signer) {
    return undefined;
  }
  const network = await signer.provider?.getNetwork();
  if (!network?.chainId) {
    return;
  }
  const chainIdFromProvider = network.chainId;
  if (!chainIdFromProvider) {
    return undefined;
  }
  return Number(chainIdFromProvider);
}

interface AddEthereumChainParameter {
  chainId: string; // A 0x-prefixed hexadecimal string
  chainName: string;
  nativeCurrency: {
    name: string;
    symbol: string; // 2-6 characters long
    decimals: 18;
  };
  rpcUrls: string[];
  blockExplorerUrls?: string[];
  iconUrls?: string[]; // Currently ignored.
}

type SwitchNetworkParams = {
  chainName: string;
  chainId: number;
  chainRpc: string;
  nativeAssetSymbol?: string;
  blockExplorerUrl: string;
};

// METAMASK bugs: https://github.com/MetaMask/metamask-mobile/issues/3546
const changeNetworkRequest = (chainId: SwitchNetworkParams['chainId']) => ({
  method: 'wallet_switchEthereumChain',
  // TODO: is there on ethers alternative to: hexStripZeros & toBeHex ? https://github.com/ethers-io/ethers.js/discussions/4002
  params: [{ chainId: toQuantity(chainId) }],
});

const addNetworkRequest = ({
  chainName,
  chainId,
  chainRpc,
  nativeAssetSymbol,
  blockExplorerUrl,
}: SwitchNetworkParams) => ({
  method: 'wallet_addEthereumChain',
  params: [
    {
      chainId: toQuantity(chainId),
      chainName,
      rpcUrls: [chainRpc],
      iconUrls: [],
      nativeCurrency: nativeAssetSymbol
        ? {
            name: nativeAssetSymbol,
            symbol: nativeAssetSymbol,
            decimals: NATIVE_ASSET_DECIMALS,
          }
        : undefined,
      blockExplorerUrls: [blockExplorerUrl],
    } as AddEthereumChainParameter,
  ],
});

const CHAIN_NOT_EXIST_ERROR_CODE = 4902;

/** "Invalid chain" err code 32603 in MetaMask - But we have to handle */
const INVALID_CHAIN_ERROR_CODE = 32603;

/**
 * Switch network chain
 * 
 * https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
 * https://docs.metamask.io/guide/rpc-api.html#unrestricted-methods
 * https://ethereum-magicians.org/t/eip-3326-wallet-switchethereumchain/5471
 * https://docs.ethers.io/v5/api/providers/other/#Web3Provider--ExternalProvider
 * 
 * More context:
 * https://github.com/MetaMask/metamask-extension/issues/5101
https://github.com/MetaMask/metamask-extension/pull/9724/files#diff-350f413652abc9e8a83cda9890d5df24aa770e0c6be0bf3c3a8c882e176bf487R31
https://twitter.com/MetaMask/status/1367229353046790146
 */
const switchWalletChain = async (
  params: SwitchNetworkParams & { signer: JsonRpcSigner | BrowserProvider }
): Promise<void> => {
  console.log('[softwareWallet] Switching chain...');
  const changeNetwork = changeNetworkRequest(params.chainId);
  const addNetwork = addNetworkRequest(params);
  const { signer } = params;
  return signer.provider
    ?.send(changeNetwork.method, changeNetwork.params)
    ?.catch((error: any) => {
      // This error code indicates that the chain has not been added to MetaMask.
      if (
        error?.error?.code !== CHAIN_NOT_EXIST_ERROR_CODE &&
        error?.code !== CHAIN_NOT_EXIST_ERROR_CODE &&
        // Mobile MetaMask has it wrapped
        error?.data?.originalError?.code !== CHAIN_NOT_EXIST_ERROR_CODE &&
        // Lets search whole string to be 100% sure
        !error?.toString().includes(CHAIN_NOT_EXIST_ERROR_CODE) &&
        error?.code !== INVALID_CHAIN_ERROR_CODE
      ) {
        console.warn(error);
        console.log('Auto-switch error - halting process');
        return;
      }
      console.log('Auto-switch continue - adding new chain');
      // We have to add new chain
      return (
        signer.provider
          ?.send(addNetwork.method, addNetwork.params)
          // eslint-disable-next-line promise/no-nesting
          ?.catch((error: unknown) => {
            console.warn(error);
            console.log(
              'Cannot auto-switch chain. User have to switch manually.'
            );
          })
      );
    });
};

/**
 * With WalletConnect there is problem using standard signer
 *  (`the method wallet_switchEthereumChain does not exist/is not available`),
 * so we will use it's custom provider instead.
 *
 * AI Commented: MetaMask Mobile via WalletConnect v2 routes every RPC call over the namespace
 * of the provider's CURRENT chainId. If the wallet connects on a different chain,
 * `wallet_switchEthereumChain` is sent over the old namespace and MetaMask Mobile
 * rejects it with `-32602: Active chainId is 0xA but received 0xB`. To work around
 * that we call `wallet_addEthereumChain` first - MetaMask handles add + switch in
 * a single user approval and is happy to receive that request on the old namespace.
 * After the switch propagates, the trailing `wallet_switchEthereumChain` is a no-op
 * or correctly switches if the chain was already known to the wallet.
 */
const switchWalletConnectChain = async (
  params: SwitchNetworkParams & { provider: EthereumProviderType }
): Promise<unknown> => {
  console.log(
    '[WalletConnect] Switching chain to ',
    params.chainId,
    `(current provider chainId: ${params.provider.chainId})`
  );
  const changeNetwork = changeNetworkRequest(params.chainId);
  const addNetwork = addNetworkRequest(params);
  const { provider } = params;

  if (Number(provider.chainId) === Number(params.chainId)) {
    return;
  }

  await provider.request(addNetwork).catch(resultAdd => {
    console.warn('[WalletConnect] wallet_addEthereumChain failed', resultAdd);
  });

  await waitAsync(300);

  return provider.request(changeNetwork).catch(resultSwitch => {
    console.warn(
      '[WalletConnect] wallet_switchEthereumChain failed',
      resultSwitch
    );
  });
};

export const triggerWalletChainSwitchIfPossible = async (
  walletType: WalletType | null,
  chain: ChainConfig,
  signer: Signer | JsonRpcSigner | BrowserProvider
): Promise<void> => {
  if (IS_TEST_ENVIRONMENT) {
    return;
  }
  if (!walletType) {
    throw new Error('Unknown wallet type while trying to switch network');
  }
  // Private Key doesnt have chain information
  if (walletType === 'privateKey' || walletType === 'keystore' || !walletType) {
    return;
  }

  const chainRpc =
    chain.chainRpcSoftwareWallet === true
      ? chain.chainRpc
      : chain.chainRpcSoftwareWallet;

  if (walletType === 'walletConnect') {
    const provider = await initializeWalletConnect();
    await switchWalletConnectChain({
      chainId: chain.chainId,
      chainRpc,
      chainName: chain.chainNetwork,
      nativeAssetSymbol: chain.chainCurrency,
      blockExplorerUrl: chain.chainBlockExplorer,
      provider,
    }).catch(error => {
      console.warn(error);
    });
    return;
  }

  // TODO: Needed extra coinbase Wallet switch ? (same as triggerWalletConnectChainSwitchIfPossible)
  //   If Coinbase implementing web3 interface correctly it should not be needed.
  // if (walletType === 'coinbaseWallet' && !(signer instanceof BrowserProvider)) {
  //   //
  //   debugger;
  //   throw new Error(
  //     'Internal error - coinbaseWallet not using BrowserProvider'
  //   );
  // } else
  if (
    !(signer instanceof JsonRpcSigner) &&
    !(signer instanceof BrowserProvider)
  ) {
    throw new Error('Internal error - wallet not using correct Signer');
  }

  try {
    await switchWalletChain({
      chainId: chain.chainId,
      chainRpc,
      chainName: chain.chainNetwork,
      nativeAssetSymbol: chain.chainCurrency,
      blockExplorerUrl: chain.chainBlockExplorer,
      signer,
    });

    await resetWeb3();

    // Most wallets watch on chain change, but need small delay to propagate wallet reset
    await waitAsync(500);
  } catch (error) {
    console.warn(error);
  }
};

// export const triggerWalletConnectChainSwitchIfPossible = async (
//   chain: ChainConfig,
//   provider: EthereumProviderType
// ): Promise<void> => {
//   try {
//     await switchWalletConnectChain({
//       chainId: chain.chainId,
//       chainRpc:
//         chain.chainRpcSoftwareWallet === true
//           ? chain.chainRpc
//           : chain.chainRpcSoftwareWallet,
//       chainName: chain.chainNetwork,
//       nativeAssetSymbol: chain.chainCurrency,
//       blockExplorerUrl: chain.chainBlockExplorer,
//       provider,
//     });

//     await resetWalletConnect();

//     // Most wallets watch on chain change, but need small delay to propagate wallet reset
//     await waitAsync(500);
//   } catch (error) {
//     console.warn(error);
//   }
// };
